import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Avatar,
  Chip,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  Breadcrumbs,
  Paper,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fade,
  Collapse
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  GitHub as GitHubIcon,
  Person as PersonIcon,
  Code as CodeIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuthToken } from "../TokenContext";
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
// Styled Components
const MainContainer = styled(Box)(({ theme, collapsed }) => ({
  width: 'auto',
  maxWidth: '100%',
  marginTop: "5rem",
  marginLeft: collapsed ? "60px" : "240px",
  padding: '24px',
  background: theme.palette.mode === 'dark' ? theme.palette.background.default : '#f6f9fc',
  minHeight: 'calc(100vh - 5rem)',
  transition: 'margin-left 0.3s ease'
}));

const PageCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  overflow: 'hidden',
  border: 'none',
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 4px 24px 0 rgba(0,0,0,0.2)' 
    : '0 4px 24px 0 rgba(34,41,47,0.1)',
  background: theme.palette.mode === 'dark' 
    ? theme.palette.background.paper 
    : '#fff',
  marginBottom: '24px'
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  padding: '32px',
  background: theme.palette.mode === 'dark'
    ? `linear-gradient(135deg, ${theme.palette.primary.dark}20 0%, ${theme.palette.primary.main}10 100%)`
    : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: '16px'
}));

const TeamMemberCard = styled(Card)(({ theme, isEditing }) => ({
  marginBottom: '16px',
  borderRadius: '12px',
  background: theme.palette.mode === 'dark' 
    ? theme.palette.background.paper 
    : '#fff',
  border: isEditing 
    ? `2px solid ${theme.palette.primary.main}`
    : `1px solid ${theme.palette.divider}`,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 2px 8px rgba(0,0,0,0.2)'
    : '0 2px 8px rgba(0,0,0,0.05)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: theme.palette.mode === 'dark'
      ? '0 4px 12px rgba(0,0,0,0.3)'
      : '0 4px 12px rgba(0,0,0,0.1)',
    transform: 'translateY(-2px)'
  }
}));

const ActionButton = styled(Button)(({ theme, variant = 'contained', color = 'primary' }) => ({
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: '8px',
  padding: '8px 16px',
  boxShadow: variant === 'contained' 
    ? theme.palette.mode === 'dark'
      ? '0 2px 8px rgba(0,0,0,0.3)'
      : '0 2px 8px rgba(0,0,0,0.1)'
    : 'none',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: variant === 'contained'
      ? theme.palette.mode === 'dark'
        ? '0 4px 12px rgba(0,0,0,0.4)'
        : '0 4px 12px rgba(0,0,0,0.15)'
      : 'none'
  }
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    background: theme.palette.mode === 'dark' 
      ? theme.palette.background.default
      : theme.palette.background.paper,
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    }
  },
  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,
    '&.Mui-focused': {
      color: theme.palette.primary.main,
    }
  }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    background: theme.palette.mode === 'dark' 
      ? theme.palette.background.default
      : theme.palette.background.paper,
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    }
  },
  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,
    '&.Mui-focused': {
      color: theme.palette.primary.main,
    }
  }
}));

const InfoChip = styled(Chip)(({ theme }) => ({
  borderRadius: '6px',
  fontWeight: 500,
  '&.MuiChip-filled': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? theme.palette.primary.dark
      : theme.palette.primary.main,
    color: theme.palette.mode === 'dark'
      ? theme.palette.primary.contrastText
      : theme.palette.primary.contrastText,
  }
}));

function Mastergithub({ collapsed = false, ...props }) {
  const theme = useTheme();
  const token = useAuthToken();
  const intl = useIntl();
 const location = useLocation();
  const { podid, podName ,podsize} = location.state || {};

  // State Management
  const [candidates, setCandidates] = useState([]);
  const [candidateNames, setCandidateNames] = useState([]);
  const [editingCandidateId, setEditingCandidateId] = useState(null);
  const [editedCandidateName, setEditedCandidateName] = useState('');
  const [editedCandidateID, setEditedCandidateID] = useState(null);
  const [editedCandidates, setEditedCandidates] = useState({});
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    candidateId: null,
    action: null
  });
  const [expandedCards, setExpandedCards] = useState({});

  const [editedCandidateData, setEditedCandidateData] = useState({
    Pod_Roles: '',
    time_zone: '', 
    region: '',
    github_username: '',
    jira_username: '',
    Primary_Skill: '',
    Secondary_Skill: '',
    Availability: '',
    github_repo: '',
    bitbucket_repo_slug: "",
    bitbucket_username: "",
    sonarqube_project_key: '',
  });

  // Configuration Arrays
  const podRolesOptions = ['Scrum Master', 'Lead', 'Product Owner', 'Developer', 'Tester', 'DevOps'];
  const timezone = [
    'Indian Standard Time',
    'Eastern Standard Time',
    'Central Standard Time',
    'Mountain Standard Time',
    'Pacific Standard Time',
    'Greenwich Mean Time',
    'Central European Time',
    'Western European Time',
    'Australian Eastern Standard Time',
    'Australian Central Standard Time',
    'Australian Western Standard Time',
    'New York Time (Eastern Daylight Time)',
    'Los Angeles Time (Pacific Daylight Time)',
    'London Time (British Summer Time)',
    'Paris Time (Central European Summer Time)',
    'Tokyo Time (Japan Standard Time)',
    'Sydney Time (Australian Eastern Daylight Time)',
    'Dubai Time (Gulf Standard Time)',
    'Moscow Time (Moscow Standard Time)',
    'Brazil Time (Brasilia Time)',
    'Chicago Time (Central Daylight Time)',
    'Houston Time (Central Daylight Time)',
    'Cape Town Time (South Africa Standard Time)'
  ];
  const region = ['Asia Pacific', 'Latin America', 'North America'];
  const primarySkillOptions = ['html', 'css', 'JavaScript', 'Adobe Illustrator', 'ReactJS', 'AngularJS', 'Java', 'Node.js', 'Vue', 'Next', 'Typescript', 'Gatsby', 'Three.js'];
  const secondarySkillOptions = ['Java', 'Python', 'PHP', 'SQL', 'Git', 'c++', 'Perl', 'AngularJS', 'Kotlin', 'AWS', 'NodeJs', 'Azure'];
  const availabilityOptions = ['full time', 'part time'];

  // Effects
  useEffect(() => {
    if (podid) {
      fetchPodCandidates();
      fetchCandidates();
    }
  }, [podid, token]);

  useEffect(() => {
    if (editingCandidateId !== null) {
      const selectedCandidate = candidates.find(candidate => candidate.candidateID === editingCandidateId);
      if (selectedCandidate) {
        setEditedCandidateData({
          Pod_Roles: selectedCandidate.Pod_Roles || '',
          region: selectedCandidate.region || '',
          time_zone: selectedCandidate.time_zone || '',
          github_username: selectedCandidate.github_username || '',
          jira_username: selectedCandidate.jira_username || '',
          Primary_Skill: selectedCandidate.Primary_Skill || '',
          Secondary_Skill: selectedCandidate.Secondary_Skill || '',
          Availability: selectedCandidate.Availability || '',
          github_repo: selectedCandidate.github_repo || '',
          bitbucket_repo_slug: selectedCandidate.bitbucket_repo_slug || '',
          bitbucket_username: selectedCandidate.bitbucket_username || '',
          sonarqube_project_key: selectedCandidate.sonarqube_project_key || '',
        });
        setEditedCandidateName(selectedCandidate.Candidate_Name || '');
      }
    } else {
      resetEditingState();
    }
  }, [editingCandidateId, candidates]);

  // Helper Functions
  const resetEditingState = () => {
    setEditedCandidateData({
      Pod_Roles: '',
      region: '',
      time_zone: '',
      github_username: '',
      jira_username: '',
      Primary_Skill: '',
      Secondary_Skill: '',
      Availability: '',
      github_repo: '',
      bitbucket_repo_slug: '',
      bitbucket_username: '',
      sonarqube_project_key: "",
    });
    setEditedCandidateName('');
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCardExpand = (candidateId) => {
    setExpandedCards(prev => ({
      ...prev,
      [candidateId]: !prev[candidateId]
    }));
  };

  // API Functions
  const fetchPodCandidates = async () => {
    try {
      const response = await fetch(
        process.env.REACT_APP_CLIENT_FETCHCANDIDATESBASEDONPODS,
        {
          method: 'POST',
          body: JSON.stringify({ pod_id: podid || null }),
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      setCandidateNames(data.data);
    } catch (error) {
      console.error('Error fetching candidates:', error);
      showSnackbar('Error fetching candidates', 'error');
    }
  };

  const fetchCandidates = async () => {
    setLoading(true);
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
    
    try {
      const response = await axios.post(process.env.REACT_APP_CLIENT_PODCANDIDATEFETCHING, {
        pods_id:podid || null,
      }, { headers });
     
      setCandidates(response.data.data || []);
    } catch (error) {
      console.error('Error fetching candidates:', error);
      showSnackbar('Error fetching candidates', 'error');
    } finally {
      setLoading(false);
    }
  };

  const updateCandidate = async (candidateId, id) => {
    setLoading(true);
    try {
      const candidateToUpdate = candidates.find(
        (candidate) => candidate.candidateID === candidateId
      );
     
      if (!candidateToUpdate) {
        // New candidate creation
        const newCandidatePayload = {
          podID:podid,
          candidateID: editedCandidateID || null,
          Pod_Roles: editedCandidateData.Pod_Roles || '',
          time_zone: editedCandidateData.time_zone || '', 
          region: editedCandidateData.region || '',
          github_username: editedCandidateData.github_username || '',
          jira_username: editedCandidateData.jira_username || '',
          github_repo: editedCandidateData.github_repo || '',
          bitbucket_repo_slug: editedCandidateData.bitbucket_repo_slug || '',
          bitbucket_username: editedCandidateData.bitbucket_username || '',
          sonarqube_project_key: editedCandidateData.sonarqube_project_key || '',
          Primary_Skill: editedCandidateData.Primary_Skill || '',
          Secondary_Skill: editedCandidateData.Secondary_Skill || '',
          Availability: editedCandidateData.Availability || '',
          Candidate_Name: editedCandidateName || null,
        };

        await axios.put(
          process.env.REACT_APP_CLIENT_PODROLES,
          newCandidatePayload,
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
          }
        );
        showSnackbar('New team member added successfully', 'success');
      } else {
        // Existing candidate update
        const editedCandidateIDToSend = editedCandidateID === "null" ? null : editedCandidateID;
        const editedCandidateNameToSend = editedCandidateName === "null" ? null : editedCandidateName;
        
        const payload = {
          podID: candidateToUpdate.podID,
          candidateID: editedCandidateIDToSend,
          id: id,
          status: candidateToUpdate.status,
          pod_status: candidateToUpdate.pod_status,
          pod_approved: candidateToUpdate.pod_approved,
          Pod_Roles: editedCandidateData.Pod_Roles || candidateToUpdate.Pod_Roles,
          time_zone: editedCandidateData.time_zone || candidateToUpdate.time_zone, 
          region: editedCandidateData.region || candidateToUpdate.region,
          github_username: editedCandidateData.github_username || candidateToUpdate.github_username,
          jira_username: editedCandidateData.jira_username || candidateToUpdate.jira_username,
          github_repo: editedCandidateData.github_repo || candidateToUpdate.github_repo,
          bitbucket_repo_slug: editedCandidateData.bitbucket_repo_slug || candidateToUpdate.bitbucket_repo_slug,
          bitbucket_username: editedCandidateData.bitbucket_username || candidateToUpdate.bitbucket_username,
          sonarqube_project_key: editedCandidateData.sonarqube_project_key || candidateToUpdate.sonarqube_project_key,
          Primary_Skill: editedCandidateData.Primary_Skill || candidateToUpdate.Primary_Skill,
          Secondary_Skill: editedCandidateData.Secondary_Skill || candidateToUpdate.Secondary_Skill,
          Availability: editedCandidateData.Availability || candidateToUpdate.Availability,
          Candidate_Name: editedCandidateNameToSend,
        };

        await axios.put(
          process.env.REACT_APP_CLIENT_PODCANDIDATEUPDATE,
          JSON.stringify(payload),
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
          }
        );
        showSnackbar('Team member updated successfully', 'success');
      }

      fetchCandidates();
      setEditingCandidateId('');
      resetEditingState();
    } catch (error) {
      console.error('Error updating/creating candidate:', error);
      showSnackbar('Error updating team member', 'error');
    } finally {
      setLoading(false);
    }
  };

  const deleteCandidate = async (podID, candidateID) => {
    setLoading(true);
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
    
    try {
      const apiUrl = process.env.REACT_APP_CLIENT_PODCANDIDATEDELETE;
      const payload = {
        podID: podID,
        candidateId: candidateID
      };

      await axios.delete(apiUrl, {
        data: payload,
        headers: headers
      });

      fetchCandidates();
      showSnackbar('Team member removed successfully', 'success');
      setConfirmDialog({ open: false, candidateId: null, action: null });
    } catch (error) {
      console.error('Error deleting candidate:', error);
      showSnackbar('Error removing team member', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Create empty rows for missing slots
  const emptyRowCount = Math.max(0, (podsize || 0) - (candidates ? candidates.length : 0));
  const emptyRows = Array.from({ length: emptyRowCount }, (_, index) => ({
    id: `empty-${index}`,
    Candidate_Name: '',
    isEmpty: true
  }));

  const mergedRows = [...candidates, ...emptyRows];

  // Render Functions
  const renderHeader = () => (
    <HeaderSection>
      <Box>
        <Typography variant="h4" fontWeight="700" gutterBottom>
          Team Configuration
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage team member roles, skills, and integration settings
        </Typography>
      </Box>
      
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <InfoChip 
          icon={<PersonIcon />}
          label={`${candidates.length}/${podsize || 0} Members`}
          variant="filled"
        />
        <ActionButton
          startIcon={<AddIcon />}
          variant="contained"
          color="primary"
          onClick={() => {
            const firstEmpty = mergedRows.find(row => row.isEmpty);
            if (firstEmpty) {
              setEditingCandidateId(firstEmpty.id);
            }
          }}
        >
          Add Member
        </ActionButton>
      </Box>
    </HeaderSection>
  );

  const renderCandidateCard = (candidate) => {
    const isEditing = editingCandidateId === candidate.id;
    const isExpanded = expandedCards[candidate.id];
    const isEmpty = candidate.isEmpty;

    return (
      <TeamMemberCard key={candidate.id} isEditing={isEditing}>
        <CardContent sx={{ p: 3 }}>
          {/* Header Row */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{ 
                  width: 48, 
                  height: 48, 
                  bgcolor: isEmpty ? 'grey.300' : 'primary.main',
                  border: `2px solid ${theme.palette.background.paper}`,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
              >
                {isEmpty ? <AddIcon /> : (candidate.Candidate_Name?.[0]?.toUpperCase() || 'T')}
              </Avatar>
              
              <Box>
                {isEditing ? (
                  <StyledFormControl fullWidth size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Select Team Member</InputLabel>
                    <Select
                      value={editedCandidates[candidate.id]?.editedCandidateID || ''}
                      onChange={(e) => {
                        const selectedCandidateID = parseInt(e.target.value, 10) || null;
                        const selectedCandidate = candidateNames.find(
                          (item) => item.candidateID === selectedCandidateID
                        );

                        setEditedCandidates((prev) => ({
                          ...prev,
                          [candidate.id]: {
                            editedCandidateID: selectedCandidateID,
                            editedCandidateName: selectedCandidate?.Candidate_Name || '',
                          },
                        }));

                        setEditedCandidateID(selectedCandidateID);
                        setEditedCandidateName(selectedCandidate?.Candidate_Name || '');
                      }}
                      displayEmpty
                    >
                      <MenuItem value="" disabled={!candidate.Candidate_Name}>
                        {candidate.Candidate_Name ? candidate.Candidate_Name : "Select Team Member"}
                      </MenuItem>
                      {candidateNames?.map((candidateOption) => (
                        <MenuItem key={candidateOption.candidateID} value={candidateOption.candidateID}>
                          {candidateOption.Candidate_Name}
                        </MenuItem>
                      ))}
                    </Select>
                  </StyledFormControl>
                ) : (
                  <Box>
                    <Typography variant="h6" fontWeight="600">
                      {isEmpty ? 'Empty Slot' : (candidate.Candidate_Name && candidate.Candidate_Name !== 'null' ? candidate.Candidate_Name : 'Team Member')}
                    </Typography>
                    {!isEmpty && candidate.Pod_Roles && (
                      <InfoChip 
                        label={candidate.Pod_Roles}
                        size="small"
                        
                        sx={{ mt: 0.5 }}
                      />
                    )}
                  </Box>
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              {!isEmpty && !isEditing && (
                <IconButton
                  onClick={() => handleCardExpand(candidate.id)}
                  size="small"
                  sx={{ 
                    bgcolor: 'action.hover',
                    '&:hover': { bgcolor: 'action.selected' }
                  }}
                >
                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              )}
              
              {isEditing ? (
                <>
                  <ActionButton
                    size="small"
                    startIcon={<SaveIcon />}
                    onClick={() => updateCandidate(candidate.candidateID, candidate.id)}
                    disabled={loading}
                  >
                    Save
                  </ActionButton>
                  <ActionButton
                    size="small"
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={() => {
                      setEditingCandidateId('');
                      resetEditingState();
                    }}
                  >
                    Cancel
                  </ActionButton>
                </>
              ) : (
                <>
                  <ActionButton
                    size="small"
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => {
                      setEditingCandidateId(candidate.id);
                      if (!isEmpty) {
                        setEditedCandidateData({
                          Pod_Roles: candidate.Pod_Roles || '',
                          Primary_Skill: candidate.Primary_Skill || '',
                          time_zone: candidate.time_zone || '', 
                          region: candidate.region || '',
                          Secondary_Skill: candidate.Secondary_Skill || '',
                          Availability: candidate.Availability || '',
                          github_username: candidate.github_username || '',
                          jira_username: candidate.jira_username || '',
                          github_repo: candidate.github_repo || '',
                          sonarqube_project_key: candidate.sonarqube_project_key || '',
                          bitbucket_repo_slug: candidate.bitbucket_repo_slug || '',
                          bitbucket_username: candidate.bitbucket_username || '',
                        });
                      }
                    }}
                  >
                    {isEmpty ? 'Add' : 'Edit'}
                  </ActionButton>
                  
                  {!isEmpty && (
                    <ActionButton
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => setConfirmDialog({
                        open: true,
                        candidateId: candidate.candidateID,
                        podId: candidate.podID,
                        action: 'delete'
                      })}
                    >
                      Remove
                    </ActionButton>
                  )}
                </>
              )}
            </Box>
          </Box>

          {/* Expanded Content or Editing Fields */}
          <Collapse in={isEditing || isExpanded} timeout="auto" unmountOnExit>
            <Divider sx={{ my: 2 }} />
            
            {isEditing ? (
              <Grid container spacing={3}>
                {/* Role and Skills */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                    Role & Skills
                  </Typography>
                  <Stack spacing={2}>
                    <StyledFormControl fullWidth>
                      <InputLabel>Pod Role</InputLabel>
                      <Select
                        value={editedCandidateData.Pod_Roles}
                        onChange={(e) => setEditedCandidateData({
                          ...editedCandidateData,
                          Pod_Roles: e.target.value
                        })}
                      >
                        {podRolesOptions.map((role) => (
                          <MenuItem key={role} value={role}>{role}</MenuItem>
                        ))}
                      </Select>
                    </StyledFormControl>

                    <StyledFormControl fullWidth>
                      <InputLabel>Primary Skill</InputLabel>
                      <Select
                        value={editedCandidateData.Primary_Skill}
                        onChange={(e) => setEditedCandidateData({
                          ...editedCandidateData,
                          Primary_Skill: e.target.value
                        })}
                      >
                        {primarySkillOptions.map((skill) => (
                          <MenuItem key={skill} value={skill}>{skill}</MenuItem>
                        ))}
                      </Select>
                    </StyledFormControl>

                    <StyledFormControl fullWidth>
                      <InputLabel>Secondary Skill</InputLabel>
                      <Select
                        value={editedCandidateData.Secondary_Skill}
                        onChange={(e) => setEditedCandidateData({
                          ...editedCandidateData,
                          Secondary_Skill: e.target.value
                        })}
                      >
                        {secondarySkillOptions.map((skill) => (
                          <MenuItem key={skill} value={skill}>{skill}</MenuItem>
                        ))}
                      </Select>
                    </StyledFormControl>
                  </Stack>
                </Grid>

                {/* Location & Availability */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                    Location & Availability
                  </Typography>
                  <Stack spacing={2}>
                    <StyledFormControl fullWidth>
                      <InputLabel>Region</InputLabel>
                      <Select
                        value={editedCandidateData.region}
                        onChange={(e) => setEditedCandidateData({
                          ...editedCandidateData,
                          region: e.target.value
                        })}
                      >
                        {region.map((r) => (
                          <MenuItem key={r} value={r}>{r}</MenuItem>
                        ))}
                      </Select>
                    </StyledFormControl>

                    <StyledFormControl fullWidth>
                      <InputLabel>Time Zone</InputLabel>
                      <Select
                        value={editedCandidateData.time_zone}
                        onChange={(e) => setEditedCandidateData({
                          ...editedCandidateData,
                          time_zone: e.target.value
                        })}
                      >
                        {timezone.map((tz) => (
                          <MenuItem key={tz} value={tz}>{tz}</MenuItem>
                        ))}
                      </Select>
                    </StyledFormControl>

                    <StyledFormControl fullWidth>
                      <InputLabel>Availability</InputLabel>
                      <Select
                        value={editedCandidateData.Availability}
                        onChange={(e) => setEditedCandidateData({
                          ...editedCandidateData,
                          Availability: e.target.value
                        })}
                      >
                        {availabilityOptions.map((availability) => (
                          <MenuItem key={availability} value={availability}>{availability}</MenuItem>
                        ))}
                      </Select>
                    </StyledFormControl>
                  </Stack>
                </Grid>

                {/* Integration Settings */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                    Integration Settings
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <StyledTextField
                        fullWidth
                        label="GitHub Username"
                        value={editedCandidateData.github_username}
                        onChange={(e) => setEditedCandidateData({
                          ...editedCandidateData,
                          github_username: e.target.value
                        })}
                        InputProps={{
                          startAdornment: <GitHubIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <StyledTextField
                        fullWidth
                        label="GitHub Repository"
                        value={editedCandidateData.github_repo}
                        onChange={(e) => setEditedCandidateData({
                          ...editedCandidateData,
                          github_repo: e.target.value
                        })}
                        InputProps={{
                          startAdornment: <CodeIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <StyledTextField
                        fullWidth
                        label="Jira Username"
                        value={editedCandidateData.jira_username}
                        onChange={(e) => setEditedCandidateData({
                          ...editedCandidateData,
                          jira_username: e.target.value
                        })}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <StyledTextField
                        fullWidth
                        label="SonarQube Project Key"
                        value={editedCandidateData.sonarqube_project_key}
                        onChange={(e) => setEditedCandidateData({
                          ...editedCandidateData,
                          sonarqube_project_key: e.target.value
                        })}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <StyledTextField
                        fullWidth
                        label="Bitbucket Username"
                        value={editedCandidateData.bitbucket_username}
                        onChange={(e) => setEditedCandidateData({
                          ...editedCandidateData,
                          bitbucket_username: e.target.value
                        })}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <StyledTextField
                        fullWidth
                        label="Bitbucket Repository Slug"
                        value={editedCandidateData.bitbucket_repo_slug}
                        onChange={(e) => setEditedCandidateData({
                          ...editedCandidateData,
                          bitbucket_repo_slug: e.target.value
                        })}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            ) : (
              // Display mode
              !isEmpty && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Role & Skills
                    </Typography>
                    <Stack spacing={1}>
                      {candidate.Primary_Skill && (
                        <InfoChip label={` ${candidate.Primary_Skill}`} size="small" />
                      )}
                      {candidate.Secondary_Skill && (
                        <InfoChip label={`Secondary: ${candidate.Secondary_Skill}`} size="small" />
                      )}
                      {candidate.Availability && (
                        <InfoChip label={candidate.Availability} size="small" />
                      )}
                    </Stack>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Location
                    </Typography>
                    <Stack spacing={1}>
                      {candidate.region && (
                        <Typography variant="body2">{candidate.region}</Typography>
                      )}
                      {candidate.time_zone && (
                        <Typography variant="body2" color="text.secondary">
                          {candidate.time_zone}
                        </Typography>
                      )}
                    </Stack>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Integration
                    </Typography>
                    <Stack spacing={1}>
                      {candidate.github_username && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <GitHubIcon fontSize="small" />
                          <Typography variant="body2">{candidate.github_username}</Typography>
                        </Box>
                      )}
                      {candidate.jira_username && (
                        <Typography variant="body2">Jira: {candidate.jira_username}</Typography>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
              )
            )}
          </Collapse>
        </CardContent>
      </TeamMemberCard>
    );
  };

  const renderEmptyState = () => (
    <Paper
      sx={{
        p: 6,
        textAlign: 'center',
        border: `2px dashed ${theme.palette.divider}`,
        borderRadius: '12px',
        bgcolor: 'transparent'
      }}
    >
      <SettingsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
      <Typography variant="h6" gutterBottom>
        No Team Members Yet
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Start by adding team members to configure their roles and settings
      </Typography>
      <ActionButton
        startIcon={<AddIcon />}
        onClick={() => {
          const firstEmpty = mergedRows.find(row => row.isEmpty);
          if (firstEmpty) {
            setEditingCandidateId(firstEmpty.id);
          }
        }}
      >
        Add First Member
      </ActionButton>
    </Paper>
  );

  return (
    <MainContainer collapsed={collapsed}>
      {/* Breadcrumbs */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs>
          <Link 
            to="/FindPodsDetails" 
            style={{ 
              color: theme.palette.primary.main, 
              textDecoration: 'none',
              fontSize: '14px',
              fontWeight: 500
            }}
          >
           Go back
          </Link>
          <Typography color="text.secondary" fontSize="14px">
            Team Configuration
          </Typography>
        </Breadcrumbs>
      </Box>

      {/* Main Content */}
      <PageCard>
        {renderHeader()}

        <Box sx={{ p: 3 }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && mergedRows.length === 0 && renderEmptyState()}

          {!loading && mergedRows.length > 0 && (
            <Stack spacing={2}>
              {mergedRows.map(renderCandidateCard)}
            </Stack>
          )}
        </Box>
      </PageCard>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, candidateId: null, action: null })}
        PaperProps={{
          sx: { borderRadius: '12px', p: 1 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Confirm Removal
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove this team member? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmDialog({ open: false, candidateId: null, action: null })}>
            Cancel
          </Button>
          <ActionButton
            color="error"
            onClick={() => deleteCandidate(confirmDialog.podId, confirmDialog.candidateId)}
            disabled={loading}
          >
            Remove
          </ActionButton>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          elevation={6}
          variant="filled"
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ borderRadius: '8px' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainContainer>
  );
}

export default Mastergithub;